import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  FileText, Plus, Edit2, Trash2, Eye, EyeOff, Save, X,
  Search, Loader2, Image, Star, Calendar, User, Clock,
  ExternalLink, Upload, Palette, Video, Globe, Type, 
  ChevronRight, Play, Check, AlertCircle, ToggleLeft, ToggleRight
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';

const FONT_OPTIONS = [
  { value: 'default', label: 'Default (System)' },
  { value: 'serif', label: 'Serif (Georgia)' },
  { value: 'sans', label: 'Sans-Serif (Inter)' },
  { value: 'mono', label: 'Monospace' },
  { value: 'playfair', label: 'Playfair Display' },
  { value: 'merriweather', label: 'Merriweather' },
  { value: 'lora', label: 'Lora' },
  { value: 'poppins', label: 'Poppins' },
];

const PRESET_THEMES = [
  { name: 'Dark Default', bg: '#000000', title: '#ffffff', text: '#d1d5db', accent: '#a855f7', heading: '#ffffff', link: '#a855f7' },
  { name: 'Deep Ocean', bg: '#0c1222', title: '#e0f2fe', text: '#94a3b8', accent: '#38bdf8', heading: '#e0f2fe', link: '#38bdf8' },
  { name: 'Warm Ember', bg: '#1a0a00', title: '#fef3c7', text: '#d6d3d1', accent: '#f59e0b', heading: '#fef3c7', link: '#f59e0b' },
  { name: 'Forest Night', bg: '#0a1a0a', title: '#dcfce7', text: '#a3a3a3', accent: '#22c55e', heading: '#dcfce7', link: '#22c55e' },
  { name: 'Royal', bg: '#0f0520', title: '#f5f3ff', text: '#c4b5fd', accent: '#8b5cf6', heading: '#f5f3ff', link: '#8b5cf6' },
  { name: 'Crimson Night', bg: '#1a0505', title: '#fecdd3', text: '#d1d5db', accent: '#f43f5e', heading: '#fecdd3', link: '#f43f5e' },
  { name: 'Light Clean', bg: '#ffffff', title: '#111827', text: '#4b5563', accent: '#6366f1', heading: '#111827', link: '#6366f1' },
  { name: 'Light Warm', bg: '#fffbeb', title: '#1c1917', text: '#57534e', accent: '#d97706', heading: '#1c1917', link: '#d97706' },
];

const DEFAULT_DESIGN = {
  page_bg_color: '#000000',
  title_color: '#ffffff',
  text_color: '#d1d5db',
  accent_color: '#a855f7',
  heading_color: '#ffffff',
  link_color: '#a855f7',
  font_family: 'default',
};

const DEFAULT_SEO = {
  seo_title: '',
  seo_description: '',
  seo_keywords: '',
  focus_keyword: '',
  og_image: '',
  canonical_url: '',
};

// ---------- Color Picker Field ----------
const ColorField = ({ label, value, onChange }) => (
  <div className="flex items-center gap-3">
    <div className="relative">
      <input
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-9 h-9 rounded-lg border border-white/20 cursor-pointer bg-transparent p-0.5"
      />
    </div>
    <div className="flex-1">
      <p className="text-sm text-gray-300">{label}</p>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="text-xs text-gray-500 bg-transparent border-none p-0 w-20 focus:outline-none font-mono"
      />
    </div>
  </div>
);

// ---------- SEO Score Indicator ----------
const SEOScore = ({ formData, seo }) => {
  let score = 0;
  const checks = [];
  
  if (seo.seo_title) { score += 20; checks.push({ pass: true, text: 'Meta title set' }); }
  else checks.push({ pass: false, text: 'Missing meta title' });
  
  if (seo.seo_description && seo.seo_description.length >= 50) { score += 20; checks.push({ pass: true, text: 'Meta description adequate' }); }
  else checks.push({ pass: false, text: 'Meta description too short or missing' });
  
  if (seo.focus_keyword) { score += 20; checks.push({ pass: true, text: 'Focus keyword set' }); }
  else checks.push({ pass: false, text: 'Missing focus keyword' });
  
  if (seo.focus_keyword && formData.title.toLowerCase().includes(seo.focus_keyword.toLowerCase())) { score += 20; checks.push({ pass: true, text: 'Focus keyword in title' }); }
  else if (seo.focus_keyword) checks.push({ pass: false, text: 'Focus keyword not in title' });
  else checks.push({ pass: false, text: 'Set focus keyword first' });
  
  if (formData.image || seo.og_image) { score += 20; checks.push({ pass: true, text: 'OG image available' }); }
  else checks.push({ pass: false, text: 'No OG image set' });
  
  const color = score >= 80 ? 'text-green-400' : score >= 50 ? 'text-yellow-400' : 'text-red-400';
  const bgColor = score >= 80 ? 'bg-green-500/20' : score >= 50 ? 'bg-yellow-500/20' : 'bg-red-500/20';
  
  return (
    <div className={`rounded-xl p-4 border border-white/10 ${bgColor}`}>
      <div className="flex items-center gap-3 mb-3">
        <div className={`text-2xl font-bold ${color}`}>{score}/100</div>
        <p className="text-sm text-gray-400">SEO Score</p>
      </div>
      <div className="space-y-1.5">
        {checks.map((c, i) => (
          <div key={i} className="flex items-center gap-2 text-xs">
            {c.pass ? <Check className="h-3 w-3 text-green-400" /> : <AlertCircle className="h-3 w-3 text-red-400" />}
            <span className={c.pass ? 'text-gray-400' : 'text-red-300'}>{c.text}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const BlogManagement = ({ authToken }) => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showEditor, setShowEditor] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [editorTab, setEditorTab] = useState('content');
  const [vodVideos, setVodVideos] = useState([]);
  const [videoSearch, setVideoSearch] = useState('');
  
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    category: '',
    author: 'LTV Editorial Team',
    read_time: '5 min read',
    image: '',
    content: '',
    is_featured: false,
    is_published: true,
  });
  const [design, setDesign] = useState({ ...DEFAULT_DESIGN });
  const [seo, setSeo] = useState({ ...DEFAULT_SEO });
  const [featuredVideoId, setFeaturedVideoId] = useState('');
  const [embeddedVideoIds, setEmbeddedVideoIds] = useState([]);
  
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  
  const categories = [
    'Industry Insights', 'Creator Economy', 'Culture', 'Streaming Guide',
    'Technical Guide', 'Creator Guide', 'News', 'Tutorials'
  ];
  
  useEffect(() => { fetchArticles(); fetchVodVideos(); }, []);
  
  const fetchArticles = async () => {
    try {
      const res = await axios.get(`${API}/api/blog/articles`);
      setArticles(res.data || []);
    } catch (err) { console.error('Error fetching articles:', err); }
    finally { setLoading(false); }
  };
  
  const fetchVodVideos = async () => {
    try {
      const res = await axios.get(`${API}/api/blog/videos-for-embed`);
      setVodVideos(res.data.videos || []);
    } catch (err) { console.error('Error fetching VOD videos:', err); }
  };
  
  const [indexingStatus, setIndexingStatus] = useState(null);
  
  const handleSubmit = async () => {
    if (!formData.title || !formData.slug || !formData.content) {
      alert('Please fill in title, slug, and content');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...formData,
        design,
        seo,
        featured_video_id: featuredVideoId,
        embedded_video_ids: embeddedVideoIds,
      };
      let res;
      if (editingArticle) {
        res = await axios.put(`${API}/api/blog/articles/${editingArticle.id}`, payload);
      } else {
        res = await axios.post(`${API}/api/blog/articles`, payload);
      }
      const status = res.data?.indexing_status;
      if (status === 'queued' || status === 'queued_update') {
        setIndexingStatus('Article saved & submitted to Google, Bing, Yandex and other search engines for indexing!');
      } else if (status === 'skipped_draft') {
        setIndexingStatus('Article saved as draft. It will be indexed when published.');
      }
      setTimeout(() => setIndexingStatus(null), 6000);
      fetchArticles();
      resetForm();
      setShowEditor(false);
    } catch (err) {
      console.error('Error saving article:', err);
      alert(err.response?.data?.detail || 'Failed to save article');
    } finally { setSaving(false); }
  };
  
  const handleReindex = async (articleId) => {
    try {
      const res = await axios.post(`${API}/api/blog/reindex/${articleId}`);
      setIndexingStatus(`Re-indexing queued: ${res.data.url}`);
      setTimeout(() => setIndexingStatus(null), 5000);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to re-index');
    }
  };
  
  const handleDelete = async (articleId) => {
    if (!confirm('Are you sure you want to delete this article?')) return;
    try {
      await axios.delete(`${API}/api/blog/articles/${articleId}`);
      fetchArticles();
    } catch (err) { alert('Failed to delete article'); }
  };
  
  const handleEdit = (article) => {
    setEditingArticle(article);
    setFormData({
      title: article.title, slug: article.slug, excerpt: article.excerpt,
      category: article.category, author: article.author, read_time: article.read_time,
      image: article.image, content: article.content,
      is_featured: article.is_featured, is_published: article.is_published,
      hide_featured_image: article.hide_featured_image || false,
    });
    setDesign(article.design ? { ...DEFAULT_DESIGN, ...article.design } : { ...DEFAULT_DESIGN });
    setSeo(article.seo ? { ...DEFAULT_SEO, ...article.seo } : { ...DEFAULT_SEO });
    setFeaturedVideoId(article.featured_video_id || '');
    setEmbeddedVideoIds(article.embedded_video_ids || []);
    setEditorTab('content');
    setShowEditor(true);
  };
  
  const togglePublished = async (article) => {
    try {
      await axios.put(`${API}/api/blog/articles/${article.id}`, { is_published: !article.is_published });
      fetchArticles();
    } catch (err) { console.error('Error toggling publish status:', err); }
  };
  
  const toggleFeatured = async (article) => {
    try {
      await axios.put(`${API}/api/blog/articles/${article.id}`, { is_featured: !article.is_featured });
      fetchArticles();
    } catch (err) { console.error('Error toggling featured status:', err); }
  };
  
  const resetForm = () => {
    setEditingArticle(null);
    setFormData({ title: '', slug: '', excerpt: '', category: '', author: 'LTV Editorial Team', read_time: '5 min read', image: '', content: '', is_featured: false, is_published: true, hide_featured_image: false });
    setDesign({ ...DEFAULT_DESIGN });
    setSeo({ ...DEFAULT_SEO });
    setFeaturedVideoId('');
    setEmbeddedVideoIds([]);
    setEditorTab('content');
  };
  
  const generateSlug = (title) => title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  
  const handleTitleChange = (e) => {
    const title = e.target.value;
    setFormData(prev => ({ ...prev, title, slug: prev.slug || generateSlug(title) }));
  };
  
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { alert('Please select an image file'); return; }
    setUploading(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      uploadFormData.append('category', 'blog');
      uploadFormData.append('video_name', formData.title || formData.slug || '');
      const res = await axios.post(`${API}/api/r2/simple-upload`, uploadFormData, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      if (res.data.url) setFormData(prev => ({ ...prev, image: res.data.url }));
    } catch (err) { alert('Failed to upload image'); }
    finally { setUploading(false); }
  };
  
  const applyThemePreset = (preset) => {
    setDesign(prev => ({
      ...prev,
      page_bg_color: preset.bg,
      title_color: preset.title,
      text_color: preset.text,
      accent_color: preset.accent,
      heading_color: preset.heading,
      link_color: preset.link,
    }));
  };
  
  const filteredArticles = articles.filter(a =>
    a.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.category.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  const filteredVodVideos = vodVideos.filter(v =>
    v.title.toLowerCase().includes(videoSearch.toLowerCase())
  );
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
      </div>
    );
  }
  
  // =================== EDITOR VIEW ===================
  if (showEditor) {
    const editorTabs = [
      { id: 'content', label: 'Content', icon: FileText },
      { id: 'design', label: 'Design', icon: Palette },
      { id: 'videos', label: 'Videos', icon: Video },
      { id: 'seo', label: 'SEO', icon: Globe },
    ];
    
    return (
      <div className="space-y-4" data-testid="blog-editor">
        {/* Editor Header */}
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <FileText className="h-5 w-5 text-purple-400" />
            {editingArticle ? 'Edit Article' : 'New Article'}
          </h2>
          <div className="flex items-center gap-3">
            <Button onClick={handleSubmit} disabled={saving} className="bg-purple-600 hover:bg-purple-500" data-testid="save-article-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              {editingArticle ? 'Update' : 'Publish'}
            </Button>
            <Button variant="outline" onClick={() => { setShowEditor(false); resetForm(); }} className="border-white/20" data-testid="cancel-editor-btn">
              <X className="h-4 w-4 mr-2" /> Cancel
            </Button>
          </div>
        </div>
        
        {/* Editor Tabs */}
        <div className="flex gap-1 bg-gray-900 rounded-xl p-1 border border-gray-800">
          {editorTabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setEditorTab(tab.id)}
              data-testid={`editor-tab-${tab.id}`}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all flex-1 justify-center
                ${editorTab === tab.id ? 'bg-purple-600 text-white shadow-lg' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </div>
        
        {/* ---- CONTENT TAB ---- */}
        {editorTab === 'content' && (
          <div className="bg-gray-900 rounded-xl p-6 border border-gray-800 space-y-5">
            <div>
              <label className="block text-sm text-gray-400 mb-1.5">Title *</label>
              <Input value={formData.title} onChange={handleTitleChange} placeholder="Enter article title" className="bg-black/30 border-white/10" data-testid="article-title-input" />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1.5">URL Slug *</label>
              <Input value={formData.slug} onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))} placeholder="article-url-slug" className="bg-black/30 border-white/10" data-testid="article-slug-input" />
              <p className="text-xs text-gray-500 mt-1">/vod/blog/{formData.slug || 'article-slug'}</p>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1.5">Category *</label>
                <select value={formData.category} onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))} className="w-full px-3 py-2 bg-black/30 border border-white/10 rounded-lg text-white text-sm" data-testid="article-category-select">
                  <option value="">Select category</option>
                  {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1.5">Author</label>
                <Input value={formData.author} onChange={(e) => setFormData(prev => ({ ...prev, author: e.target.value }))} className="bg-black/30 border-white/10" />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1.5">Read Time</label>
                <Input value={formData.read_time} onChange={(e) => setFormData(prev => ({ ...prev, read_time: e.target.value }))} className="bg-black/30 border-white/10" />
              </div>
            </div>
            
            {/* Featured Image */}
            <div>
              <label className="block text-sm text-gray-400 mb-1.5">Featured Image</label>
              {formData.image ? (
                <div className="relative inline-block">
                  <img src={formData.image} alt="Featured" className="h-36 w-auto rounded-lg object-cover" />
                  <button onClick={() => setFormData(prev => ({ ...prev, image: '' }))} className="absolute -top-2 -right-2 bg-red-500 rounded-full p-1">
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ) : (
                <div className="flex gap-3 items-center">
                  <Input value={formData.image} onChange={(e) => setFormData(prev => ({ ...prev, image: e.target.value }))} placeholder="Image URL or upload" className="bg-black/30 border-white/10 flex-1" />
                  <Button variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading} className="border-white/20">
                    {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />} Upload
                  </Button>
                  <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                </div>
              )}
            </div>
            
            {/* Hide Featured Image Toggle */}
            {formData.image && (
              <div className="flex items-center justify-between bg-black/20 rounded-lg px-4 py-3 border border-white/5">
                <div>
                  <span className="text-sm text-gray-300">Hide featured image on article page</span>
                  <p className="text-xs text-gray-500">Image still shows in blog listing and SEO previews</p>
                </div>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, hide_featured_image: !prev.hide_featured_image }))}
                  data-testid="hide-featured-image-toggle"
                >
                  {formData.hide_featured_image 
                    ? <ToggleRight className="h-7 w-7 text-purple-400" />
                    : <ToggleLeft className="h-7 w-7 text-gray-500" />
                  }
                </button>
              </div>
            )}
            
            <div>
              <label className="block text-sm text-gray-400 mb-1.5">Excerpt / Summary</label>
              <textarea value={formData.excerpt} onChange={(e) => setFormData(prev => ({ ...prev, excerpt: e.target.value }))} placeholder="Brief summary shown in previews" rows={2} className="w-full px-3 py-2.5 bg-black/30 border border-white/10 rounded-lg text-white placeholder:text-gray-600 resize-none text-sm" data-testid="article-excerpt-input" />
            </div>
            
            <div>
              <label className="block text-sm text-gray-400 mb-1.5">Content * (Markdown supported)</label>
              <textarea value={formData.content} onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))} placeholder="Write your article content here..." rows={16} className="w-full px-3 py-2.5 bg-black/30 border border-white/10 rounded-lg text-white placeholder:text-gray-600 font-mono text-sm" data-testid="article-content-input" />
              <p className="text-xs text-gray-500 mt-1">Supports Markdown: **bold**, *italic*, ## Headings, - lists, [links](url)</p>
            </div>
            
            <div className="flex flex-wrap gap-6">
              <label className="flex items-center gap-2.5 cursor-pointer">
                <input type="checkbox" checked={formData.is_published} onChange={(e) => setFormData(prev => ({ ...prev, is_published: e.target.checked }))} className="w-4 h-4 rounded bg-black/30 border-white/20 text-purple-500 accent-purple-500" />
                <span className="text-sm text-gray-300">Published</span>
              </label>
              <label className="flex items-center gap-2.5 cursor-pointer">
                <input type="checkbox" checked={formData.is_featured} onChange={(e) => setFormData(prev => ({ ...prev, is_featured: e.target.checked }))} className="w-4 h-4 rounded bg-black/30 border-white/20 text-yellow-500 accent-yellow-500" />
                <span className="text-sm text-gray-300">Featured Article</span>
              </label>
            </div>
          </div>
        )}
        
        {/* ---- DESIGN TAB ---- */}
        {editorTab === 'design' && (
          <div className="grid lg:grid-cols-2 gap-4">
            {/* Controls */}
            <div className="bg-gray-900 rounded-xl p-6 border border-gray-800 space-y-5">
              <h3 className="font-semibold text-white flex items-center gap-2"><Palette className="h-4 w-4 text-purple-400" /> Page Colors</h3>
              
              {/* Theme Presets */}
              <div>
                <p className="text-xs text-gray-500 mb-2 uppercase tracking-wider">Quick Presets</p>
                <div className="grid grid-cols-4 gap-2">
                  {PRESET_THEMES.map((preset, i) => (
                    <button key={i} onClick={() => applyThemePreset(preset)} className="group relative p-2 rounded-lg border border-white/10 hover:border-purple-500/50 transition-all text-center" data-testid={`theme-preset-${i}`}>
                      <div className="flex gap-0.5 justify-center mb-1.5">
                        <div className="w-4 h-4 rounded-full border border-white/20" style={{ backgroundColor: preset.bg }} />
                        <div className="w-4 h-4 rounded-full border border-white/20" style={{ backgroundColor: preset.accent }} />
                        <div className="w-4 h-4 rounded-full border border-white/20" style={{ backgroundColor: preset.title }} />
                      </div>
                      <p className="text-[10px] text-gray-500 group-hover:text-gray-300">{preset.name}</p>
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="space-y-3 pt-2">
                <ColorField label="Page Background" value={design.page_bg_color} onChange={(v) => setDesign(prev => ({ ...prev, page_bg_color: v }))} />
                <ColorField label="Title Color" value={design.title_color} onChange={(v) => setDesign(prev => ({ ...prev, title_color: v }))} />
                <ColorField label="Heading Color" value={design.heading_color} onChange={(v) => setDesign(prev => ({ ...prev, heading_color: v }))} />
                <ColorField label="Body Text" value={design.text_color} onChange={(v) => setDesign(prev => ({ ...prev, text_color: v }))} />
                <ColorField label="Accent / Links" value={design.accent_color} onChange={(v) => setDesign(prev => ({ ...prev, accent_color: v, link_color: v }))} />
              </div>
              
              <div>
                <p className="text-xs text-gray-500 mb-2 uppercase tracking-wider">Font Family</p>
                <select value={design.font_family} onChange={(e) => setDesign(prev => ({ ...prev, font_family: e.target.value }))} className="w-full px-3 py-2 bg-black/30 border border-white/10 rounded-lg text-white text-sm" data-testid="font-family-select">
                  {FONT_OPTIONS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                </select>
              </div>
            </div>
            
            {/* Live Preview */}
            <div className="rounded-xl border border-gray-800 overflow-hidden" data-testid="design-preview">
              <div className="px-4 py-2 bg-gray-800 text-xs text-gray-400 flex items-center gap-2">
                <Eye className="h-3 w-3" /> Live Preview
              </div>
              <div className="p-6 min-h-[400px]" style={{ backgroundColor: design.page_bg_color, fontFamily: getFontStack(design.font_family) }}>
                <span className="inline-block text-xs font-medium px-2 py-0.5 rounded-full mb-3" style={{ backgroundColor: design.accent_color + '30', color: design.accent_color }}>
                  {formData.category || 'Category'}
                </span>
                <h1 className="text-2xl font-bold mb-3 leading-tight" style={{ color: design.title_color }}>
                  {formData.title || 'Your Article Title'}
                </h1>
                <div className="flex items-center gap-3 text-xs mb-4" style={{ color: design.text_color + '99' }}>
                  <span>{formData.author}</span>
                  <span>5 min read</span>
                </div>
                {formData.image && (
                  <div className="aspect-video rounded-lg overflow-hidden mb-4">
                    <img src={formData.image} alt="" className="w-full h-full object-cover" />
                  </div>
                )}
                <p className="text-sm leading-relaxed mb-3" style={{ color: design.text_color }}>
                  {formData.excerpt || 'Your article excerpt will appear here. This is a preview of how the design choices look on the actual article page.'}
                </p>
                <h2 className="text-lg font-semibold mb-2" style={{ color: design.heading_color }}>Section Heading</h2>
                <p className="text-sm leading-relaxed" style={{ color: design.text_color }}>
                  Body text with a <span style={{ color: design.link_color, textDecoration: 'underline' }}>link example</span> to show accent color.
                </p>
              </div>
            </div>
          </div>
        )}
        
        {/* ---- VIDEOS TAB ---- */}
        {editorTab === 'videos' && (
          <div className="bg-gray-900 rounded-xl p-6 border border-gray-800 space-y-5">
            <h3 className="font-semibold text-white flex items-center gap-2"><Video className="h-4 w-4 text-purple-400" /> Embed VOD Videos</h3>
            <p className="text-sm text-gray-400">Select videos from your library to embed in this article. The featured video appears prominently at the top.</p>
            
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
              <Input placeholder="Search your videos..." value={videoSearch} onChange={(e) => setVideoSearch(e.target.value)} className="bg-black/30 border-white/10 pl-10" data-testid="video-search-input" />
            </div>
            
            {/* Selected Featured Video */}
            {featuredVideoId && (() => {
              const v = vodVideos.find(vid => vid.id === featuredVideoId);
              return v ? (
                <div className="flex items-center gap-4 bg-purple-500/10 border border-purple-500/30 rounded-xl p-4">
                  <div className="relative w-28 aspect-video rounded-lg overflow-hidden bg-gray-800 flex-shrink-0">
                    {v.thumbnail_url ? <img src={v.thumbnail_url} alt="" className="w-full h-full object-cover" /> : <Play className="h-6 w-6 text-gray-600 m-auto" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <Badge className="bg-purple-500/30 text-purple-300 text-[10px] mb-1">Featured Video</Badge>
                    <p className="font-medium text-white truncate">{v.title}</p>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => setFeaturedVideoId('')} className="text-red-400 hover:text-red-300">
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : null;
            })()}
            
            {/* Additional Embedded Videos */}
            {embeddedVideoIds.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs text-gray-500 uppercase tracking-wider">Additional Embedded Videos</p>
                {embeddedVideoIds.map(vid => {
                  const v = vodVideos.find(x => x.id === vid);
                  return v ? (
                    <div key={vid} className="flex items-center gap-3 bg-gray-800/50 border border-white/5 rounded-lg p-3">
                      <div className="w-20 aspect-video rounded overflow-hidden bg-gray-800 flex-shrink-0">
                        {v.thumbnail_url ? <img src={v.thumbnail_url} alt="" className="w-full h-full object-cover" /> : <Play className="h-4 w-4 text-gray-600 m-auto" />}
                      </div>
                      <p className="text-sm text-white flex-1 truncate">{v.title}</p>
                      <Button size="sm" variant="ghost" onClick={() => setEmbeddedVideoIds(prev => prev.filter(x => x !== vid))} className="text-red-400 hover:text-red-300">
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ) : null;
                })}
              </div>
            )}
            
            {/* Video List */}
            <div className="border border-white/10 rounded-xl overflow-hidden max-h-[400px] overflow-y-auto">
              {filteredVodVideos.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <Video className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No videos found</p>
                </div>
              ) : (
                filteredVodVideos.map(v => {
                  const isFeatured = featuredVideoId === v.id;
                  const isEmbedded = embeddedVideoIds.includes(v.id);
                  return (
                    <div key={v.id} className={`flex items-center gap-3 p-3 border-b border-white/5 hover:bg-white/5 transition-colors ${isFeatured ? 'bg-purple-500/10' : isEmbedded ? 'bg-blue-500/5' : ''}`} data-testid={`video-item-${v.id}`}>
                      <div className="w-24 aspect-video rounded overflow-hidden bg-gray-800 flex-shrink-0">
                        {v.thumbnail_url ? <img src={v.thumbnail_url} alt="" className="w-full h-full object-cover" /> : <div className="flex items-center justify-center h-full"><Play className="h-5 w-5 text-gray-600" /></div>}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-white truncate">{v.title}</p>
                        <p className="text-xs text-gray-500">{v.slug}</p>
                      </div>
                      <div className="flex gap-2 flex-shrink-0">
                        <Button size="sm" variant={isFeatured ? 'default' : 'outline'}
                          onClick={() => setFeaturedVideoId(isFeatured ? '' : v.id)}
                          className={isFeatured ? 'bg-purple-600 text-white text-xs' : 'border-white/20 text-xs'}
                          data-testid={`set-featured-${v.id}`}
                        >
                          {isFeatured ? <><Check className="h-3 w-3 mr-1" /> Featured</> : <><Star className="h-3 w-3 mr-1" /> Feature</>}
                        </Button>
                        <Button size="sm" variant={isEmbedded ? 'default' : 'outline'}
                          onClick={() => {
                            if (isEmbedded) setEmbeddedVideoIds(prev => prev.filter(x => x !== v.id));
                            else setEmbeddedVideoIds(prev => [...prev, v.id]);
                          }}
                          className={isEmbedded ? 'bg-blue-600 text-white text-xs' : 'border-white/20 text-xs'}
                          data-testid={`embed-video-${v.id}`}
                        >
                          {isEmbedded ? <><Check className="h-3 w-3 mr-1" /> Embedded</> : <><Plus className="h-3 w-3 mr-1" /> Embed</>}
                        </Button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
        
        {/* ---- SEO TAB ---- */}
        {editorTab === 'seo' && (
          <div className="grid lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 bg-gray-900 rounded-xl p-6 border border-gray-800 space-y-5">
              <h3 className="font-semibold text-white flex items-center gap-2"><Globe className="h-4 w-4 text-purple-400" /> Advanced SEO</h3>
              
              <div>
                <label className="block text-sm text-gray-400 mb-1.5">Meta Title</label>
                <Input value={seo.seo_title} onChange={(e) => setSeo(prev => ({ ...prev, seo_title: e.target.value }))} placeholder={formData.title || 'Enter meta title'} className="bg-black/30 border-white/10" data-testid="seo-title-input" />
                <p className="text-xs mt-1 text-gray-500">{(seo.seo_title || formData.title || '').length}/60 characters</p>
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-1.5">Meta Description</label>
                <textarea value={seo.seo_description} onChange={(e) => setSeo(prev => ({ ...prev, seo_description: e.target.value }))} placeholder={formData.excerpt || 'Enter meta description'} rows={3} className="w-full px-3 py-2 bg-black/30 border border-white/10 rounded-lg text-white placeholder:text-gray-600 text-sm resize-none" data-testid="seo-description-input" />
                <p className="text-xs mt-1 text-gray-500">{(seo.seo_description || formData.excerpt || '').length}/160 characters</p>
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-1.5">Focus Keyword</label>
                <Input value={seo.focus_keyword} onChange={(e) => setSeo(prev => ({ ...prev, focus_keyword: e.target.value }))} placeholder="e.g., independent streaming platforms" className="bg-black/30 border-white/10" data-testid="seo-focus-keyword-input" />
                <p className="text-xs text-gray-500 mt-1">Primary keyword this article should rank for</p>
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-1.5">SEO Keywords (comma-separated)</label>
                <Input value={seo.seo_keywords} onChange={(e) => setSeo(prev => ({ ...prev, seo_keywords: e.target.value }))} placeholder="streaming, vod, video, creator" className="bg-black/30 border-white/10" data-testid="seo-keywords-input" />
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-1.5">OG Image URL</label>
                <div className="flex gap-2">
                  <Input value={seo.og_image} onChange={(e) => setSeo(prev => ({ ...prev, og_image: e.target.value }))} placeholder={formData.image || 'Custom Open Graph image URL'} className="bg-black/30 border-white/10 flex-1" data-testid="seo-og-image-input" />
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    id="blog-og-image-upload"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      setSeo(prev => ({ ...prev, _uploading: true }));
                      try {
                        const fd = new FormData();
                        fd.append('file', file);
                        fd.append('category', 'og-images');
                        fd.append('reference_name', formData.title || formData.slug || '');
                        const res = await axios.post(`${API}/api/r2/upload-image`, fd, {
                          headers: { 'Content-Type': 'multipart/form-data' }
                        });
                        setSeo(prev => ({ ...prev, og_image: res.data.url, _uploading: false }));
                      } catch (err) {
                        console.error('R2 upload error:', err);
                        alert('Failed to upload image to R2. Check your R2 settings.');
                        setSeo(prev => ({ ...prev, _uploading: false }));
                      }
                      e.target.value = '';
                    }}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    className="border-purple-500/40 text-purple-400 hover:bg-purple-500/10 flex-shrink-0"
                    onClick={() => document.getElementById('blog-og-image-upload').click()}
                    disabled={seo._uploading}
                    data-testid="blog-og-image-upload-button"
                  >
                    {seo._uploading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Upload className="h-4 w-4 mr-1" />}
                    {seo._uploading ? 'Uploading...' : 'Upload'}
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-1">1200x630 recommended. Upload to R2 or paste a URL. Falls back to featured image.</p>
                {seo.og_image && (
                  <img src={seo.og_image} alt="OG Preview" className="mt-2 max-h-32 rounded-lg border border-white/10" onError={(e) => e.target.style.display = 'none'} />
                )}
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-1.5">Canonical URL</label>
                <Input value={seo.canonical_url} onChange={(e) => setSeo(prev => ({ ...prev, canonical_url: e.target.value }))} placeholder={`https://ltvproduction.com/vod/blog/${formData.slug}`} className="bg-black/30 border-white/10" data-testid="seo-canonical-input" />
              </div>
              
              {/* Google Preview */}
              <div>
                <p className="text-xs text-gray-500 mb-2 uppercase tracking-wider">Google Search Preview</p>
                <div className="bg-white rounded-lg p-4">
                  <p className="text-sm text-green-700 truncate">ltvproduction.com &rsaquo; vod &rsaquo; blog &rsaquo; {formData.slug || 'article-slug'}</p>
                  <p className="text-lg text-blue-700 font-medium truncate hover:underline cursor-pointer">
                    {seo.seo_title || formData.title || 'Article Title'} | LTV VOD Blog
                  </p>
                  <p className="text-sm text-gray-600 line-clamp-2">
                    {seo.seo_description || formData.excerpt || 'Article description will appear here in search results...'}
                  </p>
                </div>
              </div>
            </div>
            
            {/* SEO Score Sidebar */}
            <div className="space-y-4">
              <SEOScore formData={formData} seo={seo} />
              
              <div className="bg-gray-900 rounded-xl p-4 border border-gray-800">
                <h4 className="text-sm font-medium text-gray-300 mb-3">SEO Tips</h4>
                <ul className="space-y-2 text-xs text-gray-500">
                  <li className="flex items-start gap-2"><ChevronRight className="h-3 w-3 mt-0.5 text-purple-400 flex-shrink-0" /> Keep meta title under 60 characters</li>
                  <li className="flex items-start gap-2"><ChevronRight className="h-3 w-3 mt-0.5 text-purple-400 flex-shrink-0" /> Meta description should be 50-160 characters</li>
                  <li className="flex items-start gap-2"><ChevronRight className="h-3 w-3 mt-0.5 text-purple-400 flex-shrink-0" /> Include focus keyword in your title</li>
                  <li className="flex items-start gap-2"><ChevronRight className="h-3 w-3 mt-0.5 text-purple-400 flex-shrink-0" /> Use focus keyword in the first paragraph</li>
                  <li className="flex items-start gap-2"><ChevronRight className="h-3 w-3 mt-0.5 text-purple-400 flex-shrink-0" /> OG image should be 1200x630px for best sharing</li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
  
  // =================== LIST VIEW ===================
  return (
    <div className="space-y-6" data-testid="blog-list-view">
      {/* Indexing Status Toast */}
      {indexingStatus && (
        <div className="fixed top-4 right-4 z-50 bg-green-600/90 text-white px-5 py-3 rounded-xl shadow-2xl flex items-center gap-3 animate-in slide-in-from-right" data-testid="indexing-toast">
          <Globe className="h-5 w-5 flex-shrink-0" />
          <span className="text-sm font-medium">{indexingStatus}</span>
          <button onClick={() => setIndexingStatus(null)} className="ml-2 text-white/70 hover:text-white"><X className="h-4 w-4" /></button>
        </div>
      )}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <FileText className="h-6 w-6 text-purple-400" /> Blog Articles
          </h2>
          <p className="text-gray-400 mt-1">{articles.length} articles total</p>
        </div>
        <div className="flex gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
            <Input placeholder="Search articles..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="bg-gray-800 border-gray-700 pl-10 w-64" data-testid="blog-search-input" />
          </div>
          <Button onClick={() => setShowEditor(true)} className="bg-purple-600 hover:bg-purple-500" data-testid="new-article-btn">
            <Plus className="h-4 w-4 mr-2" /> New Article
          </Button>
        </div>
      </div>
      
      <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-800/50">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Article</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Category</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Status</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Date</th>
                <th className="px-6 py-4 text-right text-sm font-medium text-gray-400">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {filteredArticles.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                    <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No articles found</p>
                    <p className="text-sm mt-2">Create your first article to get started</p>
                  </td>
                </tr>
              ) : (
                filteredArticles.map((article) => (
                  <tr key={article.id} className="hover:bg-gray-800/30 transition-colors" data-testid={`article-row-${article.id}`}>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-4">
                        {article.image ? (
                          <img src={article.image} alt="" className="w-16 h-10 rounded object-cover" />
                        ) : (
                          <div className="w-16 h-10 rounded bg-gray-800 flex items-center justify-center"><Image className="h-5 w-5 text-gray-600" /></div>
                        )}
                        <div>
                          <p className="font-medium text-white line-clamp-1">{article.title}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            {article.featured_video_id && <Badge className="bg-blue-500/20 text-blue-300 text-[10px] border-blue-500/30">Has Video</Badge>}
                            {article.design && <Badge className="bg-pink-500/20 text-pink-300 text-[10px] border-pink-500/30">Custom Design</Badge>}
                            {article.seo?.focus_keyword && <Badge className="bg-green-500/20 text-green-300 text-[10px] border-green-500/30">SEO</Badge>}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <Badge className="bg-purple-500/20 text-purple-300 border-purple-500/30">{article.category}</Badge>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {article.is_published ? (
                          <Badge className="bg-green-500/20 text-green-300 border-green-500/30"><Eye className="h-3 w-3 mr-1" /> Published</Badge>
                        ) : (
                          <Badge className="bg-gray-500/20 text-gray-300 border-gray-500/30"><EyeOff className="h-3 w-3 mr-1" /> Draft</Badge>
                        )}
                        {article.is_featured && <Badge className="bg-yellow-500/20 text-yellow-300 border-yellow-500/30"><Star className="h-3 w-3 mr-1" /> Featured</Badge>}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-400">{new Date(article.created_at).toLocaleDateString()}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <Button size="sm" variant="ghost" onClick={() => toggleFeatured(article)} className={article.is_featured ? 'text-yellow-400' : 'text-gray-400'} title={article.is_featured ? 'Remove from featured' : 'Mark as featured'}>
                          <Star className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => togglePublished(article)} className={article.is_published ? 'text-green-400' : 'text-gray-400'}>
                          {article.is_published ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                        </Button>
                        <a href={`/vod/blog/${article.slug}`} target="_blank" rel="noopener noreferrer" className="p-2 text-gray-400 hover:text-white transition-colors">
                          <ExternalLink className="h-4 w-4" />
                        </a>
                        {article.is_published && (
                          <Button size="sm" variant="ghost" onClick={() => handleReindex(article.id)} className="text-blue-400 hover:text-blue-300" title="Re-index on search engines" data-testid={`reindex-article-${article.id}`}>
                            <Globe className="h-4 w-4" />
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => handleEdit(article)} data-testid={`edit-article-${article.id}`}>
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDelete(article.id)} className="text-red-400 hover:text-red-300" data-testid={`delete-article-${article.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gray-900 rounded-xl p-4 border border-gray-800">
          <p className="text-sm text-gray-400">Total Articles</p>
          <p className="text-2xl font-bold text-white">{articles.length}</p>
        </div>
        <div className="bg-gray-900 rounded-xl p-4 border border-gray-800">
          <p className="text-sm text-gray-400">Published</p>
          <p className="text-2xl font-bold text-green-400">{articles.filter(a => a.is_published).length}</p>
        </div>
        <div className="bg-gray-900 rounded-xl p-4 border border-gray-800">
          <p className="text-sm text-gray-400">Drafts</p>
          <p className="text-2xl font-bold text-gray-400">{articles.filter(a => !a.is_published).length}</p>
        </div>
        <div className="bg-gray-900 rounded-xl p-4 border border-gray-800">
          <p className="text-sm text-gray-400">Featured</p>
          <p className="text-2xl font-bold text-yellow-400">{articles.filter(a => a.is_featured).length}</p>
        </div>
      </div>
    </div>
  );
};

function getFontStack(fontFamily) {
  const map = {
    'default': '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    'serif': 'Georgia, "Times New Roman", serif',
    'sans': '"Inter", "Helvetica Neue", Arial, sans-serif',
    'mono': '"Fira Code", "Courier New", monospace',
    'playfair': '"Playfair Display", Georgia, serif',
    'merriweather': '"Merriweather", Georgia, serif',
    'lora': '"Lora", Georgia, serif',
    'poppins': '"Poppins", "Helvetica Neue", sans-serif',
  };
  return map[fontFamily] || map['default'];
}

export default BlogManagement;
